'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { parseShellUa } from '@/lib/runtime/is-native-app';
import { getReportButtonLabels, type ReportReasonKey } from '@/lib/ui-content/report-button-content';

/**
 * Fleet-wide content-report affordance (App Store 1.2 / Play UGC + AI-content:
 * "report or flag offensive content without needing to exit the app").
 * Mounted from both footers, so every generated page carries it. Anonymous;
 * posts to /api/report (rate-limited, lands in content_reports for the
 * 24h-triage commitment documented in /terms).
 *
 * Its copy lives in lib/ui-content/report-button-content.ts (24 languages);
 * a language without a record falls back to English.
 */

type ReasonKey = ReportReasonKey;

const REASON_KEYS: ReasonKey[] = [
  'inaccurate-or-fabricated',
  'wrong-product',
  'sexually-explicit',
  'regulated-product',
  'dangerous-product',
  'ip-infringement',
  'other',
];

export function ReportContentButton({ languageCode }: { languageCode: string }) {
  const t = getReportButtonLabels(languageCode);
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState<ReasonKey | null>(null);
  const [detail, setDetail] = useState('');
  const [state, setState] = useState<'idle' | 'sending' | 'sent' | 'failed'>('idle');
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const submit = useCallback(async () => {
    if (!reason || state === 'sending') return;
    setState('sending');
    try {
      const shell = parseShellUa(navigator.userAgent);
      const res = await fetch('/api/report', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          url: window.location.pathname,
          locale: window.location.pathname.split('/')[1] ?? null,
          reason,
          detail: detail || undefined,
          source: shell.platform ? (`app-${shell.platform}` as const) : 'web',
        }),
      });
      setState(res.ok ? 'sent' : 'failed');
    } catch {
      setState('failed');
    }
  }, [reason, detail, state]);

  return (
    <>
      <button
        type="button"
        onClick={() => {
          setOpen(true);
          setState('idle');
          setReason(null);
          setDetail('');
        }}
        className="inline-flex items-center gap-1.5 hover:opacity-80"
      >
        <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2z"
          />
        </svg>
        {t.report}
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[100] flex items-end justify-center bg-black/50 p-4 sm:items-center"
          onClick={(e) => {
            if (e.target === e.currentTarget) setOpen(false);
          }}
        >
          <div
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-label={t.title}
            className="w-full max-w-md rounded-2xl border border-neutral-900/10 bg-light p-5 shadow-xl dark:border-white/10 dark:bg-dark"
          >
            {state === 'sent' ? (
              <div className="flex flex-col items-center gap-4 py-4 text-center">
                <p className="text-sm">{t.sent}</p>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="rounded-full border-2 border-black bg-white/95 px-6 py-2 text-sm font-medium text-black dark:border-white"
                >
                  OK
                </button>
              </div>
            ) : (
              <>
                <p className="mb-3 text-sm font-semibold">{t.title}</p>
                <div className="flex flex-col gap-2">
                  {REASON_KEYS.map((key) => (
                    <label key={key} className="flex cursor-pointer items-center gap-2 text-sm">
                      <input
                        type="radio"
                        name="report-reason"
                        checked={reason === key}
                        onChange={() => setReason(key)}
                        className="accent-sky-500"
                      />
                      {t.reasons[key]}
                    </label>
                  ))}
                </div>
                <textarea
                  value={detail}
                  onChange={(e) => setDetail(e.target.value.slice(0, 2000))}
                  placeholder={t.detail}
                  rows={2}
                  className="mt-3 w-full rounded-lg border border-neutral-900/15 bg-transparent p-2 text-sm dark:border-white/15"
                />
                {state === 'failed' && <p className="mt-2 text-sm text-red-500">{t.failed}</p>}
                <div className="mt-4 flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setOpen(false)}
                    className="px-4 py-2 text-sm text-zinc-600 hover:opacity-80 dark:text-zinc-400"
                  >
                    {t.cancel}
                  </button>
                  <button
                    type="button"
                    onClick={submit}
                    disabled={!reason || state === 'sending'}
                    className="rounded-full border-2 border-black bg-white/95 px-5 py-2 text-sm font-medium text-black disabled:opacity-40 dark:border-white"
                  >
                    {t.submit}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
